import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AiSearchAutoEvalViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  runIdValue?: { displayId: string; id: string } | null;
  runId: string;
  queryIndexValue?: { displayId: string; id: string } | null;
  queryIndex: number;
  conditionValue?: { displayId: string; id: string } | null;
  condition: string;
  includeSchemaValue?: { displayId: string; id: string } | null;
  includeSchema: boolean;
  includeExamplesValue?: { displayId: string; id: string } | null;
  includeExamples: boolean;
  queryValue?: { displayId: string; id: string } | null;
  query: string;
  pathTypeValue?: { displayId: string; id: string } | null;
  pathType: string;
  expectedToolValue?: { displayId: string; id: string } | null;
  expectedTool: string;
  expectedEntityValue?: { displayId: string; id: string } | null;
  expectedEntity: string;
  actualToolValue?: { displayId: string; id: string } | null;
  actualTool: string;
  actualEntityValue?: { displayId: string; id: string } | null;
  actualEntity: string;
  actualArgsValue?: { displayId: string; id: string } | null;
  actualArgs: string;
  l1PassValue?: { displayId: string; id: string } | null;
  l1Pass: boolean;
  l2PassValue?: { displayId: string; id: string } | null;
  l2Pass: boolean;
  errorTypeValue?: { displayId: string; id: string } | null;
  errorType: string;
  totalTimeMsValue?: { displayId: string; id: string } | null;
  totalTimeMs: number;
  llmCallCountValue?: { displayId: string; id: string } | null;
  llmCallCount: number;
  toolCallCountValue?: { displayId: string; id: string } | null;
  toolCallCount: number;
  tokenCountValue?: { displayId: string; id: string } | null;
  tokenCount: number | undefined;
  llmResponseValue?: { displayId: string; id: string } | null;
  llmResponse: string;
  toolCallsValue?: { displayId: string; id: string } | null;
  toolCalls: string;
  evaluatedAtValue?: { displayId: string; id: string } | null;
  evaluatedAt: Date | null;
  hadDisambiguationValue?: { displayId: string; id: string } | null;
  hadDisambiguation: boolean;
  disambiguationTypeValue?: { displayId: string; id: string } | null;
  disambiguationType: string;
  disambiguationResponseValue?: { displayId: string; id: string } | null;
  disambiguationResponse: string;
  patternsAppliedValue?: { displayId: string; id: string } | null;
  patternsApplied: string[];
  l3EfficiencyValue?: { displayId: string; id: string } | null;
  l3Efficiency: number | undefined;
  l4RedundancyValue?: { displayId: string; id: string } | null;
  l4Redundancy: number | undefined;
  l5DisambiguationValue?: { displayId: string; id: string } | null;
  l5Disambiguation: number | undefined;
  l6ResponseQualityValue?: { displayId: string; id: string } | null;
  l6ResponseQuality: number | undefined;
  overallScoreValue?: { displayId: string; id: string } | null;
  overallScore: number | undefined;
  judgeEvaluationValue?: { displayId: string; id: string } | null;
  judgeEvaluation: string;
  redundantCallsValue?: { displayId: string; id: string } | null;
  redundantCalls: number | undefined;
  actualCallCountValue?: { displayId: string; id: string } | null;
  actualCallCount: number | undefined;
  optimalCallCountValue?: { displayId: string; id: string } | null;
  optimalCallCount: number | undefined;
  sequenceMatchedOptimalValue?: { displayId: string; id: string } | null;
  sequenceMatchedOptimal: boolean;
  responseValue?: { displayId: string; id: string } | null;
  response: string;
};

export const AiSearchAutoEvalViewForm: React.FC<AiSearchAutoEvalViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      runId = "",
      queryIndex = 0,
      condition = "",
      includeSchema = false,
      includeExamples = false,
      query = "",
      pathType = "",
      expectedTool = "",
      expectedEntity = "",
      actualTool = "",
      actualEntity = "",
      actualArgs = "{}",
      l1Pass = false,
      l2Pass = false,
      errorType = "",
      totalTimeMs = 0,
      llmCallCount = 0,
      toolCallCount = 0,
      tokenCount = 0,
      llmResponse = "",
      toolCalls = "{}",
      evaluatedAt = null,
      hadDisambiguation = false,
      disambiguationType = "",
      disambiguationResponse = "",
      patternsApplied = [],
      l3Efficiency = 0,
      l4Redundancy = 0,
      l5Disambiguation = 0,
      l6ResponseQuality = 0,
      overallScore = 0,
      judgeEvaluation = "{}",
      redundantCalls = 0,
      actualCallCount = 0,
      optimalCallCount = 0,
      sequenceMatchedOptimal = false,
      response = "",
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupAiSearchAutoEvalLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={runId}
                  label={langStore.AiSearchAutoEvalRunIdLabel}
                />
                <ShowNumber
                  value={queryIndex}
                  label={langStore.AiSearchAutoEvalQueryIndexLabel}
                />
                <ShowMultiLine
                  value={condition}
                  label={langStore.AiSearchAutoEvalConditionLabel}
                />
                <ShowBoolean
                  value={includeSchema}
                  label={langStore.AiSearchAutoEvalIncludeSchemaLabel}
                />
                <ShowBoolean
                  value={includeExamples}
                  label={langStore.AiSearchAutoEvalIncludeExamplesLabel}
                />
                <ShowMultiLine
                  value={query}
                  label={langStore.AiSearchAutoEvalQueryLabel}
                />
                <ShowMultiLine
                  value={pathType}
                  label={langStore.AiSearchAutoEvalPathTypeLabel}
                />
                <ShowMultiLine
                  value={expectedTool}
                  label={langStore.AiSearchAutoEvalExpectedToolLabel}
                />
                <ShowMultiLine
                  value={expectedEntity}
                  label={langStore.AiSearchAutoEvalExpectedEntityLabel}
                />
                <ShowMultiLine
                  value={actualTool}
                  label={langStore.AiSearchAutoEvalActualToolLabel}
                />
                <ShowMultiLine
                  value={actualEntity}
                  label={langStore.AiSearchAutoEvalActualEntityLabel}
                />
                <InputCodeEditor
                  value={actualArgs}
                  onChange={() => {}}
                  label={langStore.AiSearchAutoEvalActualArgsLabel}
                  language="json"
                  readOnly={true}
                />
                <ShowBoolean
                  value={l1Pass}
                  label={langStore.AiSearchAutoEvalL1PassLabel}
                />
                <ShowBoolean
                  value={l2Pass}
                  label={langStore.AiSearchAutoEvalL2PassLabel}
                />
                <ShowMultiLine
                  value={errorType}
                  label={langStore.AiSearchAutoEvalErrorTypeLabel}
                />
                <ShowNumber
                  value={totalTimeMs}
                  label={langStore.AiSearchAutoEvalTotalTimeMsLabel}
                />
                <ShowNumber
                  value={llmCallCount}
                  label={langStore.AiSearchAutoEvalLlmCallCountLabel}
                />
                <ShowNumber
                  value={toolCallCount}
                  label={langStore.AiSearchAutoEvalToolCallCountLabel}
                />
                <ShowNumber
                  value={tokenCount}
                  label={langStore.AiSearchAutoEvalTokenCountLabel}
                />
                <ShowMultiLine
                  value={llmResponse}
                  label={langStore.AiSearchAutoEvalLlmResponseLabel}
                />
                <InputCodeEditor
                  value={toolCalls}
                  onChange={() => {}}
                  label={langStore.AiSearchAutoEvalToolCallsLabel}
                  language="json"
                  readOnly={true}
                />
                <ShowDateTime
                  value={evaluatedAt}
                  label={langStore.AiSearchAutoEvalEvaluatedAtLabel}
                />
                <ShowBoolean
                  value={hadDisambiguation}
                  label={langStore.AiSearchAutoEvalHadDisambiguationLabel}
                />
                <ShowMultiLine
                  value={disambiguationType}
                  label={langStore.AiSearchAutoEvalDisambiguationTypeLabel}
                />
                <ShowMultiLine
                  value={disambiguationResponse}
                  label={langStore.AiSearchAutoEvalDisambiguationResponseLabel}
                />
                <ShowMultiLine
                  value={patternsApplied}
                  label={langStore.AiSearchAutoEvalPatternsAppliedLabel}
                />
                <ShowNumber
                  value={l3Efficiency}
                  label={langStore.AiSearchAutoEvalL3EfficiencyLabel}
                />
                <ShowNumber
                  value={l4Redundancy}
                  label={langStore.AiSearchAutoEvalL4RedundancyLabel}
                />
                <ShowNumber
                  value={l5Disambiguation}
                  label={langStore.AiSearchAutoEvalL5DisambiguationLabel}
                />
                <ShowNumber
                  value={l6ResponseQuality}
                  label={langStore.AiSearchAutoEvalL6ResponseQualityLabel}
                />
                <ShowNumber
                  value={overallScore}
                  label={langStore.AiSearchAutoEvalOverallScoreLabel}
                />
                <InputCodeEditor
                  value={judgeEvaluation}
                  onChange={() => {}}
                  label={langStore.AiSearchAutoEvalJudgeEvaluationLabel}
                  language="json"
                  readOnly={true}
                />
                <ShowNumber
                  value={redundantCalls}
                  label={langStore.AiSearchAutoEvalRedundantCallsLabel}
                />
                <ShowNumber
                  value={actualCallCount}
                  label={langStore.AiSearchAutoEvalActualCallCountLabel}
                />
                <ShowNumber
                  value={optimalCallCount}
                  label={langStore.AiSearchAutoEvalOptimalCallCountLabel}
                />
                <ShowBoolean
                  value={sequenceMatchedOptimal}
                  label={langStore.AiSearchAutoEvalSequenceMatchedOptimalLabel}
                />
                <ShowMultiLine
                  value={response}
                  label={langStore.AiSearchAutoEvalResponseLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
